import {WsServerConnection} from "./serverConnection/wsServerConnection";
import {DataSourceManager} from "./dataSources/DataSourceManager";
import {UsersDataSource} from "./dataSources/UsersDataSource";
import {User} from "../utils/typescript/interfaces";
import {ClientUser} from "../API";


import {action, computed, makeObservable, observable} from "mobx";
import {
    getFullRequestKey,
    handleHttpLikeRequestTroughWebsocketResponse,
    requestLockMap
} from "./serverConnection/makeApiServerRequest";

import {ActivityProvider} from "./ActivityProvider";

// Platform service imports (optional for backward compatibility)
import { IStorageService } from "../platform/services/storage/StorageService";
import { FileProvider } from "../utils/file/FileProvider";
import {AppState} from "@stores/AppState";


export type UserRole = 'owner' | 'admin' | 'reader' | 'editor';


export class UserSessionState {
    public wsServerConnection: WsServerConnection;
    public dsManager?: DataSourceManager;
    public usersDS: UsersDataSource;
    public user: User;
    public currentUserAvatarSrc?: string;
    public currentUserId?: string;
    public activityProvider: ActivityProvider;
    public fileProvider: FileProvider;
    appState: AppState;

    // Injected services (required)
    private storageService: IStorageService;

    constructor(
        appState: AppState,
        storageService: IStorageService,
        user: User,
        getIsOnline: () => boolean
    ) {
        this.wsServerConnection = new WsServerConnection(appState.getServiceRegistry().getAuthService());
        this.appState = appState;
        this.storageService = storageService;
        this.user = user;
        this.activityProvider = new ActivityProvider(appState.rootStore, this.user["custom:clientId"], this.wsServerConnection);
        this.fileProvider = new FileProvider(storageService);
        this.usersDS = new UsersDataSource({
            dataClientService: appState.getServiceRegistry().getDataClientService(),
            authService: appState.getServiceRegistry().getAuthService(),
            userState: this,
        })

        this.wsServerConnection.watchNetworkState(getIsOnline);

        this.wsServerConnection.onMsg((msg) => {
            // check if there is a waiting http like request
            if (msg.a) {
                for (let [requestKey, requestState] of requestLockMap) {
                    const responseKey = getFullRequestKey(msg.a, requestState.getUniqueRequestHash(msg));
                    if (responseKey === requestKey) {
                        handleHttpLikeRequestTroughWebsocketResponse(msg, requestKey, requestState);
                    }
                }
            }
        })

        makeObservable({
            currentUserDb: computed,
            currentUserRole: computed,
            availableWorkspaces: computed,
            currentUserAvatarSrc: observable,
            setCurrentUserAvatarSrc: action,
        })
    }

    setCurrentUserAvatarSrc(newSrc: string) {
        this.currentUserAvatarSrc = newSrc;
    }

    async resetCurrentUserAvatarPath() {
        if (this.currentUserDb!.avatarPath) {
            const freshAvatarSignedSrc = await this.storageService.getSignedUrl(this.currentUserDb!.avatarPath);
            this.setCurrentUserAvatarSrc(freshAvatarSignedSrc);
        }
    }

    async init() {
        await this.usersDS.initialLoad();
        this.currentUserId = this.usersDS.getUserBySub(this.user.sub)?.id;
        if (!this.currentUserId) {
            throw new Error('current user is not found in the company');
        }
        this.dsManager = new DataSourceManager(
            this.appState.getServiceRegistry().getDataClientService(),
            this.appState.getServiceRegistry().getAuthService(),
            this.appState,
            this.user["custom:clientId"],
            this.currentUserId
        );
        await this.dsManager.init();

        this.wsServerConnection.connect();
        await this.resetCurrentUserAvatarPath();
    }

    get currentUserDb(): ClientUser | undefined {
        return this.usersDS.users.get(this.currentUserId);
    }

    get ownerUserId() {
        return this.dsManager?.clientAR.row?.ownerUserId;
    }

    get clientDB() {
        return this.dsManager?.clientAR.row;
    }

    get ownerUser() {
        return this.usersDS.users.get(this.ownerUserId);
    }

    get tenantId() {
        return this.user['custom:clientId'];
    }

    get availableWorkspaces() {
        const allWorkspaces = this.dsManager?.workspacesDS.filteredRows;
        if (!allWorkspaces?.length) {
            return [];
        }
        if (this.isCurrentUserOwner) {
            return allWorkspaces
        }

        const currentUserWsIds = this.dsManager!.userWorkspacesDS.allRows.map(wsUserDB => wsUserDB.workspaceId);
        return allWorkspaces
            .filter(workspaceDB => currentUserWsIds.includes(workspaceDB.id))
    }

    getWsById(id: string) {
        return this.availableWorkspaces.find(ws => ws.id === id) || null
    }

    getWsBySlug(slug: string) {
        return this.availableWorkspaces.find(ws => ws.slug === slug) || null
    }

    getUserRole = (user: ClientUser): UserRole => {
        if (user.id === this.ownerUserId) {
            return 'owner';
        } else if (user.isAdmin) {
            return 'admin';
        } else if (user.isReadOnly) {
            return 'reader';
        } else {
            return 'editor';
        }
    }

    get currentUserRole(): UserRole {
        const user = this.currentUserDb;
        if (!user) {
            return 'reader';
        }
        return this.getUserRole(user);
    }

    get isCurrentUserReadOnly() {
        return this.currentUserRole === 'reader';
    }

    get isCurrentUserAdmin() {
        const currentUserRole = this.currentUserRole;
        return currentUserRole === 'admin' || currentUserRole === 'owner';
    }

    get isCurrentUserOwner() {
        const clientAR = this.dsManager?.clientAR;
        if (!clientAR) {
            return false;
        }
        return this.currentUserId === clientAR.row?.ownerUserId;
    }

    destroy() {
        this.activityProvider.destroy();
        this.usersDS.destroy();
        this.wsServerConnection.close();
        this.dsManager?.destroy();
    }
}
